<template>
  <Dialog
    :visible="visible"
    @update:visible="handleVisibilityChange"
    modal
    :header="title"
    :style="{ width: '50rem' }"
    :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
    class="app-info-dialog"
  >
    <div class="space-y-4">
      <!-- Application Version -->
      <div class="flex justify-between items-center p-3 bg-surface-50 dark:bg-surface-800 rounded">
        <span class="font-medium text-surface-900 dark:text-surface-0">{{ $t('principal.app_info.version') }}</span>
        <span class="text-surface-600 dark:text-surface-400">{{ appInfo.version || '1.0.0' }}</span>
      </div>

      <!-- Build Date -->
      <div class="flex justify-between items-center p-3 bg-surface-50 dark:bg-surface-800 rounded">
        <span class="font-medium text-surface-900 dark:text-surface-0">{{ $t('principal.app_info.build_date') }}</span>
        <span class="text-surface-600 dark:text-surface-400">{{ appInfo.buildDate || new Date().toLocaleDateString() }}</span>
      </div>

      <!-- Environment -->
      <div class="flex justify-between items-center p-3 bg-surface-50 dark:bg-surface-800 rounded">
        <span class="font-medium text-surface-900 dark:text-surface-0">{{ $t('principal.app_info.environment') }}</span>
        <span class="text-surface-600 dark:text-surface-400">{{ appInfo.environment || 'development' }}</span>
      </div>

      <!-- Features -->
      <div class="p-3 bg-surface-50 dark:bg-surface-800 rounded">
        <span class="font-medium text-surface-900 dark:text-surface-0 mb-2 block">{{ $t('principal.app_info.features') }}</span>
        <div class="flex flex-wrap gap-2">
          <span v-for="feature in appInfo.features" :key="feature"
                class="px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 text-xs rounded">
            {{ feature }}
          </span>
        </div>
      </div>

      <!-- Contact/Support -->
      <div class="p-3 bg-surface-50 dark:bg-surface-800 rounded">
        <span class="font-medium text-surface-900 dark:text-surface-0 mb-2 block">{{ $t('principal.app_info.support') }}</span>
        <div class="text-surface-600 dark:text-surface-400 text-sm">
          <p>{{ $t('principal.app_info.contact_info') }}</p>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import Dialog from 'primevue/dialog';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * Props for the AppInfoDialog component.
 */
const props = defineProps<{
  /** Whether the dialog is visible */
  visible: boolean;
  /** Application information data */
  appInfo: {
    version?: string;
    buildDate?: string;
    environment?: string;
    apiBaseUrl?: string;
    features?: string[];
  };
}>();

/**
 * Emits for the AppInfoDialog component.
 */
const emit = defineEmits<{
  /** Emitted when the dialog visibility changes */
  'update:visible': [visible: boolean];
}>();

/**
 * i18n composable for translations.
 */
const { t } = useI18n({
  useScope: 'global',
});

/**
 * Computed property for dialog title.
 */
const title = computed(() => t('principal.app_info.title'));

/**
 * Handles visibility changes from the Dialog component.
 */
function handleVisibilityChange(newVisible: boolean) {
  emit('update:visible', newVisible);
}
</script>

<style scoped>
/* Add any additional styles if needed */
</style>
